'use client';
import BlockHeader from '@/components/UIkit/BlockHeader';
import { useEffect, useState, ReactNode } from 'react';
import ModalForm from '../Modal/Modal';
import Image from 'next/image';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface AdvantagesCustomV2Props {
  id?: string;
  color?: 'violet' | 'green' | 'blue';
  title: ReactNode | SerializedEditorState;
  subtitle: ReactNode | SerializedEditorState;
  image: string | { url: string; alt: string };
  list: Array<{
    title: ReactNode | SerializedEditorState;
    subtitle: ReactNode | SerializedEditorState;
  }>;
  showButton?: boolean;
  buttonText?: string;
  btnTag?: string;
}

export default function AdvantagesCustomV2Image({
  title,
  subtitle,
  color = 'green',
  list,
  id,
  image = { url: '/assets/photoHero/Presents.webp', alt: '' },
  showButton,
  buttonText,
  btnTag,
}: AdvantagesCustomV2Props) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % list.length);
    }, 3000); // Adjust the timing as needed

    return () => clearInterval(interval);
  }, [list.length]);
  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);
  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';
  return (
    <>
      <div id={id} data-meta="AdvantagesCustomV2Image">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 my-14">
          <BlockHeader title={title} subtitle={subtitle} />
          <div className="max-w-7xl mx-auto flex flex-col gap-4 justify-center items-center lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="order-2 content-center mask-dir large rounded-3xl lg:order-1 lg:col-start-1 lg:col-end-7 lg:gap-6">
              <Image
                width={800}
                height={500}
                className="w-full h-full object-cover"
                src={typeof image === 'string' ? image : image.url}
                alt={typeof image === 'string' ? '' : image.alt}
              />
            </div>
            <div className="lg:block lg:order-2 lg:col-start-7 lg:col-end-13 mt-8">
              {list.map((item, index) => (
                <div
                  key={index}
                  className={`vertical-divider flex justify-start ${
                    index === 0 ? `first` : ''
                  } ${index === list.length - 1 ? 'last' : ''} ${
                    activeIndex === index ? 'white active' : ''
                  }`}
                >
                  <div className="flex text-white flex-col lg:items-start mb-6">
                    <CustomRichText data={item.title} tag="div" className="text-xl font-bold" />
                    <CustomRichText data={item.subtitle} tag="div" className="block text-sm" />
                  </div>
                </div>
              ))}
              {showButton && (
                <div className="flex justify-center lg:justify-start mt-4">
                  <button
                    onClick={openForm}
                    className={`${backgroundColorClass} uppercase rounded-full px-8 py-2.5 text-center text-md font-fixel font-bold text-white shadow-sm focus-visible:outline`}
                  >
                    {buttonText}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isFormOpen && (
        <ModalForm closeForm={closeForm} btnMeta={{ tag: btnTag ?? '' }} color={color} />
      )}
    </>
  );
}
